import { ENDPOINTS as IssuesV2Endpoints } from '../../../../shared/issuesV2';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { IssuesCategorySummary, Scope } from '../../../../shared/issuesV2';
import type { TeamSelection } from '../../../types';

export const ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX = ['issues', 'categories', 'summary'];

export const getIssueCategorySummaryQueryKey = (scope: Scope, teamId: TeamSelection) => [
  ...ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX,
  { scope, teamId },
];

// Invalidated on issue scans (see `useScanRevision`) and on ignore/unignore
// (see `IssuesV2Store.actions`), so 10s keeps navigation snappy without
// serving stale counts.
const STALE_TIME = 10_000;

/**
 * Fetch issue category counts for a given scope and team selection.
 *
 * Returns the standard React Query result, including `data`, `isLoading`, `isError`, and `error`.
 * Use `ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX` to invalidate all cached category summaries,
 * or `getIssueCategorySummaryQueryKey(scope, teamId)` to invalidate a specific scope/team entry.
 *
 * @param scope - The issue scope to fetch category counts for.
 * @param teamId - The selected teams filter to apply to the summary request.
 * @param shouldFetch - Whether the query should run immediately.
 */
export function useIssueCategorySummary(scope: Scope, teamId: TeamSelection, shouldFetch = true) {
  return useQuery<IssuesCategorySummary>({
    queryKey: getIssueCategorySummaryQueryKey(scope, teamId),
    queryFn: () =>
      get<IssuesCategorySummary>(IssuesV2Endpoints.CATEGORY_SUMMARY, true, { scope, teamId }, { encodeQuery: true }),
    enabled: shouldFetch,
    staleTime: STALE_TIME,
  });
}
